import { CommonModule } from '@angular/common'
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Injector,
  OnInit,
  SecurityContext,
  Signal,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  input,
  runInInjectionContext,
  signal,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser'

import { Observable, from, map, of, switchMap } from 'rxjs'

import { FlxStyleModule, FlxThemeModule, FlxWrapperStyleDirective } from '@flowx/angular-theme'
import { FlxKeyboardDirective } from '@flowx/angular-ui-toolkit'
import {
  FileSourceType,
  ImageAccessibilityProps,
  ImageTemplateConfig,
  ProcessActionTriggerEvent,
  ResolvedAction,
  fileSourceNeedsBlobFetch,
  getActionsByTemplateConfigAndTrigger$,
  getImageBase64,
  getImageSrcFromTemplateConfig$,
  isMediaLibraryFileSource,
  isValidBase64Image,
  normalizeBase64Image,
} from '@flowx/core-sdk'

import { FlxLocalizePipe } from '../../pipes/flx-localize.pipe'
import { FlxActionService } from '../../services/flx-action.service'
import { getBoolExpressionSignal } from '../helpers'

@Component({
  selector: 'flx-image',
  hostDirectives: [
    { directive: FlxWrapperStyleDirective, inputs: ['flxWrapperStyle: templateConfig'] },
  ],
  imports: [CommonModule, FlxStyleModule, FlxKeyboardDirective, FlxThemeModule, FlxLocalizePipe],
  templateUrl: './flx-image.component.html',
  styleUrl: './flx-image.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class FlxImageComponent implements OnInit, AfterViewInit {
  #injector = inject(Injector)
  #actionService = inject(FlxActionService)
  #sanitizer = inject(DomSanitizer)
  #flxWrapperStyle = inject(FlxWrapperStyleDirective)
  #destroyRef = inject(DestroyRef)

  // actions for the CLICK trigger – kept in sync with the process-actions store
  // so they arrive even when the node's details resolve after this component mounts.
  #clickActions = signal<ResolvedAction[]>([])

  templateConfig = input.required<ImageTemplateConfig>()

  displayOptions = computed(() => this.templateConfig().displayOptions)
  flowxProps = computed(() => this.displayOptions().flowxProps)
  accessibility = computed((): ImageAccessibilityProps | null => this.flowxProps()?.accessibility)

  evaluatedConditionalStyles = signal([])
  conditionalStyles = computed(() =>
    this.evaluatedConditionalStyles()
      .filter(({ expression }) => expression())
      .map(({ overrides }) => overrides)
  )

  // expressions
  isHidden: Signal<boolean>
  alt: Signal<string>
  ariaHidden: Signal<boolean>

  imageSrc$: Observable<{ src: SafeResourceUrl; isPlaceholder: boolean } | null>

  hasClickAction = computed(() => this.#clickActions().length > 0)

  ngOnInit(): void {
    runInInjectionContext(this.#injector, () => {
      const { hide } = this.flowxProps()
      this.isHidden = getBoolExpressionSignal(hide, this.templateConfig(), { logType: 'hide' })
      this.alt = computed(() => this.accessibility()?.alt)
      this.ariaHidden = computed(() => this.accessibility()?.decorative)

      const { conditionals } = this.displayOptions().style
      if (Array.isArray(conditionals)) {
        this.evaluatedConditionalStyles.set(
          conditionals.map(({ condition, overrides }, index) => ({
            expression: getBoolExpressionSignal(condition, this.templateConfig(), {
              logType: 'conditional',
              conditionIndex: index,
            }),
            overrides,
          }))
        )
      }
    })
    this.imageSrc$ = getImageSrcFromTemplateConfig$(this.templateConfig()).pipe(
      switchMap((imgSrc) => {
        const { image, placeholderUrl = '' } = this.flowxProps() ?? {}
        const sourceType = image?.sourceType
        if (isMediaLibraryFileSource(image)) {
          return of(imgSrc) // no need to fetch image -> use the src directly (unauthenticated)
        }

        if (sourceType === FileSourceType.BASE64) {
          const base64Src = normalizeBase64Image(imgSrc)
          return base64Src
            ? from(isValidBase64Image(base64Src)).pipe(
                map((isValid) => (isValid ? base64Src : placeholderUrl))
              )
            : of(placeholderUrl)
        }

        // FlowX backend images (document plugin, or any `/download` endpoint) need the
        // authenticated blob fetch and are same-origin, so no CORS concern. Plain external
        // URLs are used directly; fetching them as a blob via XHR would trigger a CORS
        // preflight that external hosts typically reject.
        if (fileSourceNeedsBlobFetch(image, imgSrc)) {
          return from(getImageBase64(imgSrc, placeholderUrl)) // fetch image (authenticated) and return base64
        }

        return of(imgSrc) // plain URL -> use directly (unauthenticated)
      }),
      map((imgSrc) => {
        const src = this.#sanitizer.sanitize(SecurityContext.URL, imgSrc)
        if (!src) {
          return null
        }
        const { placeholderUrl = '' } = this.flowxProps()
        return { src, isPlaceholder: !!placeholderUrl && imgSrc === placeholderUrl }
      })
    )

    getActionsByTemplateConfigAndTrigger$(this.templateConfig(), ProcessActionTriggerEvent.CLICK)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((actions) => this.#clickActions.set(actions))
  }

  ngAfterViewInit(): void {
    runInInjectionContext(this.#injector, () => {
      effect(() => {
        this.#flxWrapperStyle.toggleDisplay(this.isHidden())
      })
    })
  }

  async onClick(): Promise<void> {
    if (!this.hasClickAction()) {
      return
    }
    await this.#actionService.runActions(this.#clickActions(), this.templateConfig())
  }
}
